import { motion } from "framer-motion";
import { Calendar, Edit2, Music, Save, Tag, Trash2, Upload, X } from "lucide-react";
import React, { useState } from "react";
import { usePlaylistStore } from "../stores/playlistStore";
import { Track } from "../types";
import { ConfirmDialog } from "./ConfirmDialog";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

interface PlaylistManagerProps {
  playlistId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const PlaylistManager: React.FC<PlaylistManagerProps> = ({ playlistId, isOpen, onClose }) => {
  const { playlists, updatePlaylist, deletePlaylist } = usePlaylistStore();
  const playlist = playlists.find((p) => p.id === playlistId);

  const [formData, setFormData] = useState({
    name: playlist?.name || "",
    description: playlist?.description || "",
    coverUrl: playlist?.coverUrl || "",
    tags: playlist?.tags || [],
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string>(playlist?.coverUrl || "");
  const [newTag, setNewTag] = useState("");

  if (!playlist) return null;

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setCoverPreview(result);
        setFormData((prev) => ({ ...prev, coverUrl: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    updatePlaylist(playlist.id, {
      ...formData,
      updatedAt: Date.now(),
    });
    onClose();
  };

  const handleDelete = () => {
    deletePlaylist(playlist.id);
    onClose();
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const TrackItem: React.FC<{ track: Track; index: number }> = ({ track, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg group hover:bg-zinc-800"
    >
      {track.albumArt ? (
        <img src={track.albumArt} alt={track.title} className="w-12 h-12 rounded-md object-cover" />
      ) : (
        <div className="w-12 h-12 bg-zinc-700 rounded-md flex items-center justify-center">
          <Music className="w-6 h-6 text-zinc-500" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{track.title}</h4>
        <p className="text-sm text-zinc-400 truncate">{track.artist}</p>
      </div>

      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
        <Button variant="ghost" size="icon">
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" className="text-red-400">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Music className="w-6 h-6" />
            Edit Playlist
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column - Cover and Basic Info */}
          <div className="space-y-6">
            {/* Playlist Cover */}
            <div className="relative group">
              {coverPreview ? (
                <img src={coverPreview} alt="Playlist cover" className="w-full aspect-square object-cover rounded-lg" />
              ) : (
                <div className="w-full aspect-square bg-zinc-800 rounded-lg flex items-center justify-center">
                  <Music className="w-24 h-24 text-zinc-600" />
                </div>
              )}
              <Label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition cursor-pointer rounded-lg">
                <Upload className="w-12 h-12" />
                <Input type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
              </Label>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Playlist Name</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="bg-zinc-800"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="bg-zinc-800"
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Additional Info and Tracks */}
          <div className="space-y-6">
            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-zinc-800 rounded-full flex items-center gap-2 group">
                    {tag}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTag(tag)}
                      className="opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addTag()}
                  placeholder="Add a tag"
                  className="bg-zinc-800"
                />
                <Button onClick={addTag} variant="secondary">
                  Add
                </Button>
              </div>
            </div>

            {/* Playlist Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50 rounded-lg">
              {playlist.createdAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="text-sm text-zinc-400">Created</p>
                    <p className="font-medium">{new Date(playlist.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-sm text-zinc-400">Tracks</p>
                  <p className="font-medium">{playlist.tracks.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tracks Section */}
        <div className="mt-8">
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
            {playlist.tracks.map((track, index) => (
              <TrackItem key={track.id} track={track} index={index} />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <DialogFooter className="flex w-full !justify-between mt-8 pt-6 border-t border-zinc-800">
          <Button onClick={() => setShowDeleteConfirm(true)} variant="destructive">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Playlist
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-yellow-500 text-white hover:bg-yellow-600">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Playlist"
        message={`Are you sure you want to delete "${playlist.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        type="danger"
      />
    </Dialog>
  );
};
